import React from 'react';
import If from '../If';
import styled from 'styled-components';
import { MenuItem } from '#/modules/workOrder/utils/bulk-edit';
import { color } from '@nuvolo/nuux-theme/utils';
import FAIcon from '#/common/components/FAIcon';

const WIDTH = 286;
const MARGIN_BOTTOM = 75;
const MARGIN_RIGHT = 30;

const getNumericValue = (value?: string | number): number | null => {
  if (value === undefined) {
    return null;
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
};

const getPositionValue = (
  value: string | number | undefined,
  transform?: (numericValue: number) => number,
): string => {
  if (value === undefined) {
    return 'auto';
  }

  const numericValue = getNumericValue(value);
  if (numericValue === null) {
    return String(value);
  }

  const nextValue = transform ? transform(numericValue) : numericValue;
  return `${nextValue}px`;
};

const PopupWrapper = styled.div<{
  top?: string | number;
  left?: string | number;
  bottom?: string | number;
  right?: string | number;
}>`
  position: fixed;
  top: ${({ top }) => getPositionValue(top)};
  left: ${({ left }) => getPositionValue(left, (numericValue) => numericValue - WIDTH)};
  bottom: ${({ bottom }) =>
    getPositionValue(bottom, (numericValue) => numericValue + MARGIN_BOTTOM)};
  right: ${({ right }) => getPositionValue(right)};

  z-index: 9000;
  background: ${color('custom', 'invertedBackground')};
  width: ${WIDTH}px;
  height: fit-content;
  border: 1px solid ${color('primary', 'light3')};
  border-radius: 12px;
  padding: 8px 0;
  box-shadow: var(--elevation-lvl3);
  overflow: hidden;
`;

const Row = styled.div`
  height: 36px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    background: ${color('ui', 'light3')};
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 8999;
`;

type ToolbarButtonPopupProps = {
  top?: string | number;
  left?: string | number;
  right?: string | number;
  bottom?: string | number;
  visible: boolean;
  menuItems: MenuItem[];
  onClose: () => void;
  onSelected: (id: string, isEligible?: boolean) => void;
};

const ToolbarButtonPopup: React.FC<ToolbarButtonPopupProps> = ({
  top,
  left,
  right,
  bottom,
  visible,
  menuItems,
  onClose,
  onSelected,
}) => {
  return (
    <If condition={visible}>
      <PopupWrapper top={top} left={left} right={right} bottom={bottom}>
        {menuItems?.map(({ icon, label, id, isEligible }) => (
          <Row
            key={id}
            onClick={() => {
              onSelected(id, isEligible);
              onClose();
            }}>
            <div style={{ paddingRight: '8px' }}>
              <FAIcon icon={icon} size="sm" />
            </div>
            {label}
          </Row>
        ))}
      </PopupWrapper>

      <Overlay onClick={onClose} />
    </If>
  );
};

export default React.memo(ToolbarButtonPopup);
